import { Stack, Typography } from "@mui/material";
import { useBorder } from "@pulse/shared-components";
import {
  Chip,
  useCornerRadius,
  useFoundationColorTokens,
  useShadow,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import {
  MessageCircleQuestion,
  GripVertical,
  MoveRight,
  User,
} from "lucide-react";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React from "react";
import { useTranslation } from "react-i18next";
import { SurveyRuleModel } from "../../models/SurveyRuleModel";
import {
  getActionLabel,
  getConditionKey,
  getConditionValue,
  getRuleTitle,
  getTargetLabel,
} from "./utils/SurveyRuleDragOverlayCardUtils";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";

interface SurveyRuleDragOverlayCardProps {
  surveyRule: Instance<typeof SurveyRuleModel>;
}

const MAX_VISIBLE_CONDITIONS = 3;
const SURVEY_RULE_DRAG_OVERLAY_CARD_WIDTH = "520px";

export const SurveyRuleDragOverlayCard: React.FC<SurveyRuleDragOverlayCardProps> =
  observer(({ surveyRule }) => {
    const { t } = useTranslation();
    const border = useBorder();
    const cornerRadius = useCornerRadius();
    const shadow = useShadow();
    const spacing = useSpacing();
    const tokens = useFoundationColorTokens();
    const typography = useTypography();

    const conditions = surveyRule.conditionOrder.map((conditionId) =>
      surveyRule.getNonNullCondition(conditionId),
    );
    const hiddenConditionCount = Math.max(
      conditions.length - MAX_VISIBLE_CONDITIONS,
      0,
    );

    return (
      <Stack
        width={SURVEY_RULE_DRAG_OVERLAY_CARD_WIDTH}
        bgcolor={tokens.backgroundElevatedLevel1}
        border={border.subtle}
        borderRadius={cornerRadius.radiusXS}
        boxShadow={shadow.level3}
        padding={spacing.spaceMD}
        gap={spacing.spaceMD}
        divider={<DividerComponent orientation="horizontal" width="100%" />}
      >
        <Stack direction="row" alignItems="center" gap={spacing.spaceMD}>
          <GripVertical />
          <Typography flex={1} {...typography.s1}>
            {getRuleTitle(conditions, t)}
          </Typography>
          <Chip
            label={
              conditions.length === 1
                ? t("surveys.rules.singleRuleCount")
                : t("surveys.rules.multipleRuleCount", {
                    count: conditions.length,
                  })
            }
            color="primarySubtle"
          />
        </Stack>
        <Stack>
          {conditions.slice(0, MAX_VISIBLE_CONDITIONS).map((condition) => (
            <Stack
              key={condition.conditionId}
              direction="row"
              alignItems="center"
              gap={spacing.spaceSM}
              paddingBlock={spacing.spaceSM}
            >
              {condition.keyType.type === "RESPONDENT" ? (
                <User />
              ) : (
                <MessageCircleQuestion />
              )}
              <Typography noWrap flex={1} {...typography.b1}>
                {getConditionKey(condition, t)}
              </Typography>
              <Typography {...typography.s1}>=</Typography>
              <Typography noWrap flex={2} {...typography.b1}>
                {getConditionValue(condition, t)}
              </Typography>
            </Stack>
          ))}
          {hiddenConditionCount > 0 && (
            <Typography color={tokens.labelSubtle} {...typography.b2}>
              {t("surveys.rules.moreRuleConditions", {
                count: hiddenConditionCount,
              })}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={spacing.spaceSM}>
          <Typography color={tokens.label} {...typography.b1}>
            {getActionLabel(surveyRule.action, t)}
          </Typography>
          <MoveRight />
          <Typography noWrap flex={1} {...typography.b1}>
            {getTargetLabel(surveyRule, t)}
          </Typography>
        </Stack>
      </Stack>
    );
  });
